import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const dateProblemsCode = `//  Problem 1: Mutability
const deadline = new Date('2024-12-31');
deadline.setMonth(0); // Mutates the original object!
console.log(deadline.toISOString()); // 2024-01-31

//  Problem 2: 0-Indexed Months
const jan = new Date(2024, 0, 1); // January is 0? Logical nightmare.

//  Problem 3: No Duration support
// Calculating "3 months from now" requires manual date math overflow logic.`;

const temporalTypesCode = `// 1. Precise Moments (UTC)
const instant = Temporal.Instant.from("2024-01-01T12:00:00Z");

// 2. Wall-Clock Time (Timezone Aware)
const zoned = Temporal.ZonedDateTime.from({
    year: 2024,
    month: 1,
    day: 1,
    timeZone: "America/New_York"
});

// 3. Calendar Dates (No Time/Zone)
const birthday = Temporal.PlainDate.from("1995-12-25");

// 4. Durations
const upgradeGracePeriod = Temporal.Duration.from({ months: 3, days: 15 });`;

const dateArithmeticCode = `const start = Temporal.PlainDate.from("2024-01-01");

// Immutable Arithmetic
const end = start.add({ months: 6, days: 10 });
console.log(start.toString()); // Still 2024-01-01
console.log(end.toString());   // 2024-07-11

// Precision Comparisons
const isBefore = Temporal.PlainDate.compare(start, end) < 0; // true

// Duration extraction
const diff = start.until(end, { largestUnit: 'month' });
console.log(diff.months); // 6`;

const JSTemporal = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Modern Standards</div>
                <h1>Temporal: The Future of Time</h1>
                <p className="hero-lead">
                    The legacy `Date` object is widely considered JavaScript's biggest
                    architectural mistake. The **Temporal API** (Stage 3) is the modern
                    successor, providing a robust, immutable, and timezone-aware system
                    for date and time engineering.
                </p>
            </div>

            <div className="content-section">
                <h2>The Failure of `new Date()`</h2>
                <p>
                    Since 1995, JavaScript developers have struggled with the `Date`
                    object's mutability, lack of timezone support, and bizarre
                    0-indexed months. Because `Date` mutates in place, simple
                    calculations often lead to side-effect bugs that are difficult
                    to trace in complex applications.
                </p>
                <CodeBlock code={dateProblemsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Temporal Data Model</h2>
                <p>
                    Temporal introduces a distinct set of types designed to prevent
                    logical errors. Instead of one "do-everything" object, it splits
                    functionality into specialized classes: **Instant** for absolute
                    timestamps, **PlainDate** for calendars, and **ZonedDateTime**
                    for timezone-specific logic.
                </p>
                <CodeBlock code={temporalTypesCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Immutable Date Engineering</h2>
                <p>
                    Every operation in Temporal returns a *new* object. This functional
                    approach ensures that your original data remains safe. The API
                    also includes powerful `add`, `subtract`, and `until` methods
                    that handle complex calendar math (like leap years and month overflows)
                    automatically.
                </p>
                <CodeBlock code={dateArithmeticCode} language="javascript" />
            </div>

            <ProTip><p>
                    As of 2024, Temporal is in **Stage 3** of the TC39 process. This
                    means the specification is finalized, and browser vendors are
                    implementing it. To use it today in production, you must use a
                    polyfill like `@js-temporal/polyfill`. Note that because Temporal
                    is mathematically complex, the polyfill can be large (~30kb gzipped);
                    use code-splitting to load it only where needed.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Immutability:** Never worry about a date being changed by a library function.</li>
                    <li>**Precision:** Use `PlainDate` when you only care about the day (e.g., birthdays).</li>
                    <li>**Timezones:** Use `ZonedDateTime` for global scheduling to avoid the "Off-by-One" hour bug.</li>
                    <li>**Standardization:** All Temporal types parse and stringify to standard ISO 8601 formats.</li>
                    <li>**Readability:** Months are correctly 1-based (1 = January).</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSTemporal;


